import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Account, AccountInfo, Partner, ProductsWithEligibility, ShopStatus } from 'src/api/@generated/backend';

import { fetchProductsWithEligibilityActionCreator } from 'src/state/request/action-creators/fetch-products-with-eligibility-action-creator';

import {
  fetchAccountInfoActionCreator,
  fetchAccountsActionCreator,
  fetchPartnersActionCreator,
  fetchShopStatusActionCreator,
} from './action-creators';
import { RequestState, RequestStatus, RequestType } from './types';

const initialRequestState = {
  status: RequestStatus.IDLE,
};

const initialState: RequestState = {
  [RequestType.GET_ACCOUNTS]: initialRequestState,
  [RequestType.GET_SHOP_STATUS]: initialRequestState,
  [RequestType.GET_ACCOUNT_INFO]: initialRequestState,
  [RequestType.GET_PARTNERS]: initialRequestState,
  [RequestType.GET_PRODUCTS_WITH_ELIGIBILITY]: initialRequestState,
};

const requestSlice = createSlice({
  name: 'request',
  initialState,
  reducers: {
    resetRequestState: () => initialState,
    resetNewAccount: (state) => {
      state.GET_ACCOUNT_INFO = initialRequestState;
    },
    resetNewAddress: (state) => {
      state.GET_PRODUCTS_WITH_ELIGIBILITY = initialRequestState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAccountInfoActionCreator.pending, (state) => {
      state[RequestType.GET_ACCOUNT_INFO] = {
        ...state[RequestType.GET_ACCOUNT_INFO],
        status: RequestStatus.IN_PROGRESS,
      };
    });
    builder.addCase(fetchAccountInfoActionCreator.fulfilled, (state, action: PayloadAction<AccountInfo>) => {
      state[RequestType.GET_ACCOUNT_INFO] = {
        ...state[RequestType.GET_ACCOUNT_INFO],
        status: RequestStatus.SUCCESS,
        data: action.payload,
      };
    });
    builder.addCase(fetchAccountInfoActionCreator.rejected, (state) => {
      state[RequestType.GET_ACCOUNT_INFO] = {
        ...state[RequestType.GET_ACCOUNT_INFO],
        status: RequestStatus.FAILURE,
      };
    });

    builder.addCase(fetchAccountsActionCreator.pending, (state) => {
      state[RequestType.GET_ACCOUNTS] = {
        ...state[RequestType.GET_ACCOUNTS],
        status: RequestStatus.IN_PROGRESS,
      };
    });
    builder.addCase(fetchAccountsActionCreator.fulfilled, (state, action: PayloadAction<Account[]>) => {
      state[RequestType.GET_ACCOUNTS] = {
        ...state[RequestType.GET_ACCOUNTS],
        status: RequestStatus.SUCCESS,
        data: action.payload,
      };
    });
    builder.addCase(fetchAccountsActionCreator.rejected, (state) => {
      state[RequestType.GET_ACCOUNTS] = {
        ...state[RequestType.GET_ACCOUNTS],
        status: RequestStatus.FAILURE,
      };
    });

    builder.addCase(fetchPartnersActionCreator.pending, (state) => {
      state[RequestType.GET_PARTNERS] = {
        ...state[RequestType.GET_PARTNERS],
        status: RequestStatus.IN_PROGRESS,
      };
    });
    builder.addCase(fetchPartnersActionCreator.fulfilled, (state, action: PayloadAction<Partner[]>) => {
      state[RequestType.GET_PARTNERS] = {
        ...state[RequestType.GET_PARTNERS],
        status: RequestStatus.SUCCESS,
        data: action.payload,
      };
    });
    builder.addCase(fetchPartnersActionCreator.rejected, (state) => {
      state[RequestType.GET_PARTNERS] = {
        ...state[RequestType.GET_PARTNERS],
        status: RequestStatus.FAILURE,
      };
    });

    builder.addCase(fetchShopStatusActionCreator.pending, (state) => {
      state[RequestType.GET_SHOP_STATUS] = {
        ...state[RequestType.GET_SHOP_STATUS],
        status: RequestStatus.IN_PROGRESS,
      };
    });
    builder.addCase(fetchShopStatusActionCreator.fulfilled, (state, action: PayloadAction<ShopStatus>) => {
      state[RequestType.GET_SHOP_STATUS] = {
        ...state[RequestType.GET_SHOP_STATUS],
        status: RequestStatus.SUCCESS,
        data: action.payload,
      };
    });
    builder.addCase(fetchShopStatusActionCreator.rejected, (state) => {
      state[RequestType.GET_SHOP_STATUS] = {
        ...state[RequestType.GET_SHOP_STATUS],
        status: RequestStatus.FAILURE,
      };
    });

    builder.addCase(fetchProductsWithEligibilityActionCreator.pending, (state) => {
      state[RequestType.GET_PRODUCTS_WITH_ELIGIBILITY] = {
        ...state[RequestType.GET_PRODUCTS_WITH_ELIGIBILITY],
        status: RequestStatus.IN_PROGRESS,
      };
    });
    builder.addCase(
      fetchProductsWithEligibilityActionCreator.fulfilled,
      (state, action: PayloadAction<ProductsWithEligibility>) => {
        state[RequestType.GET_PRODUCTS_WITH_ELIGIBILITY] = {
          ...state[RequestType.GET_PRODUCTS_WITH_ELIGIBILITY],
          status: RequestStatus.SUCCESS,
          data: action.payload,
        };
      },
    );
    builder.addCase(fetchProductsWithEligibilityActionCreator.rejected, (state) => {
      state[RequestType.GET_PRODUCTS_WITH_ELIGIBILITY] = {
        ...state[RequestType.GET_PRODUCTS_WITH_ELIGIBILITY],
        status: RequestStatus.FAILURE,
      };
    });
  },
});

export const {
  actions: { resetNewAddress, resetNewAccount, resetRequestState },
  reducer: requestSliceReducer,
} = requestSlice;
